import {onMounted, ref, type BaseTransitionProps, type Ref, type TransitionProps} from 'vue';
import {addRouteMiddleware} from '#app';
import {useRequestHeader, useState} from '#imports';

/**
 * This composable is made to simplify and regroup logic when using slide pages transitions in apps.
 *
 * ❗️ This should be declared at the root of the parent page component (where `<NuxtPage/>` is declared in the template)
 *
 * *Usage*
 *
 * Imagine we have this page structure:
 *
 * pages
 *  ├── index.vue
 *  ├── profile.vue
 *  └── profile
 *      ├── index.vue
 *      ├── admin.vue
 *      ├── friends.vue
 *      ├── friends
 *          ├── index.vue
 *          └── baguette.vue
 *      └── likes.vue
 *
 * Here we want to display tabs for the `profile` page and it's children pages (index, admin, friends, likes).
 *
 * In the `profile.vue`, we will declare this
 *
 * ```vue
 * <template>
 *      <NuxtPage :transition="transitionProps"/>
 * </template>
 *
 * <script setup lang='ts'>
 * import {usePageTransition} from "#imports";
 *
 * /// The order (`pathsOrder`) is important here, as it has to match the order displayed in the UI to know which way the transition has to go
 * const transitionProps = usePageTransition({pathsOrder: ["/", "friends", "likes", "admin"]});
 * </script>
 * ```
 *
 * Every path you declare will take into account children routes, that means navigating from `profile/likes` to `profile/friends/baguette` will trigger the correct transition.
 *
 * ---
 *
 * ## What if the routes are in a deeper level? (like `/foo/bar/profile/friends`)
 *
 * There is an additional option to provide a base path for all routes
 *
 * ```ts
 * const transitionProps = usePageTransition({basePath: "/foo/bar/profile", pathsOrder: ["/", "friends", "likes", "admin"]});
 * ```
 *
 * ---
 *
 * ## What if my base path is dynamic? Like (`/foo/{id}/profile`)
 *
 * The base path option can also handle this case
 *
 * ```ts
 * const transitionProps = usePageTransition({basePath: "/foo/{whatever}/profile", pathsOrder: ["/", "friends", "likes", "admin"]});
 * ```
 *
 * ---
 *
 * ## What if one of my children routes is dynamic? Like (`/foo/{id}/profile/friends`)
 *
 * You can also provide arguments in the `pathsOrder` array.
 *
 * ```ts
 * const transitionProps = usePageTransition({basePath: "/foo/{whatever}/profile", pathsOrder: ["/", "{id}", "/{id}/likes", "/{id}/admin"], });
 * ```
 *
 * ## Transitions options
 *
 * You can also provide all available transition options/hooks in the composable
 *
 * ```ts
 * const transitionProps = usePageTransition({
 *      pathsOrder: ["/", "friends", "likes", "admin"],
 *      onAfterEnter: (el) => {
 *          // do stuff
 *      }
 * });
 * ```
 *
 * Exemple here `/project-client/project-client-front/src/pages/project/[id]/index.vue`
 *
 * ## How to disable page transitions
 *
 * If you want to disable page transitions, you can use the `x-malt-no-page-transitions` request header.
 *
 */
export function usePageSlideTransition(
    options: {basePath?: string; pathsOrder: string[]; animationType?: 'page-slide' | 'slide'} & Omit<BaseTransitionProps, 'mode'>,
): Ref<TransitionProps> {
    const {basePath = '', pathsOrder, animationType = 'slide', ...transitionOptions} = options;

    const noPageTransitions = useRequestHeader('x-malt-no-page-transitions');

    const state = useState<{noPageTransitions: boolean}>('use-page-slide-transition-state', () => ({
        noPageTransitions: !!noPageTransitions,
    }));

    const transitionProps = ref<TransitionProps>({});

    const filteredBaseUrl = basePath.replace(/\{.+\}/g, '\\w+');
    const pathRegex = new RegExp(`^${filteredBaseUrl}(/?[^/?#]*)(?:[/?#].*)?$`);

    function matchRouteParam(path: string, routeParam: string): boolean {
        const filteredChildPath = path.replace(/\{.+\}/g, '\\w+');
        const childPathRegex = new RegExp(`${filteredChildPath}`);
        if (path === '/') {
            return path === routeParam;
        }
        const match = routeParam.match(childPathRegex);
        if (!match) {
            return false;
        }
        return !!match[0];
    }

    onMounted(() => {
        addRouteMiddleware((to, from) => {
            if (state.value.noPageTransitions || to.meta?.pageTransition === false || from.meta?.pageTransition === false) {
                transitionProps.value = {};
                return;
            }

            let [, toRouteParam] = to.path.match(pathRegex) ?? [];
            let [, fromRouteParam] = from.path.match(pathRegex) ?? [];

            // Handle index routes
            if (toRouteParam === '') {
                toRouteParam = '/';
            }
            if (fromRouteParam === '') {
                fromRouteParam = '/';
            }

            // Handles early url replace like query params
            if (toRouteParam === fromRouteParam) {
                return true;
            }

            if (!toRouteParam || !fromRouteParam) {
                return true;
            }

            const indexTo = pathsOrder.findIndex((path) => matchRouteParam(path, toRouteParam));
            const indexFrom = pathsOrder.findIndex((path) => matchRouteParam(path, fromRouteParam));

            if (indexTo > -1 && indexFrom > -1) {
                transitionProps.value = {
                    name: undefined,
                    mode: 'out-in',
                    ...transitionOptions,
                };
                const goToNextPage = indexTo >= indexFrom;
                transitionProps.value.name = goToNextPage ? `${animationType}-next` : `${animationType}-previous`;
            } else {
                transitionProps.value = {};
            }

            return true;
        });
    });

    return transitionProps;
}
